import React from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  type BoxProps,
  CompassColor,
  Image,
  Text,
} from "@noom/wax-component-library";

import { HeaderSectionType, useProgramResult } from "@/hooks";

import { CaptionText, TagText, Tooltip } from "./shared";

const ImprovedBodyCompositionTooltip: React.FC<BoxProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Box {...props}>
      <Tooltip
        backgroundColor={CompassColor.blueberry}
        marginTop="var(--spacing-l)"
        width="156px"
      >
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.improvedBodyComposition")}
        </CaptionText>
      </Tooltip>
    </Box>
  );
};

const LowerA1cTooltip: React.FC<BoxProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Box {...props}>
      <Tooltip
        backgroundColor={CompassColor.blueberry}
        marginTop="var(--spacing-l)"
        width="128px"
      >
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.lowerA1c")}
        </CaptionText>
      </Tooltip>
    </Box>
  );
};

const GraphOverlayWeightValues = ({
  weight,
  idealWeight,
}: {
  weight?: number;
  idealWeight?: number;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        left="var(--spacing-l)"
        position="absolute"
        top="236px"
      >
        <TagText color={CompassColor.white}>
          {t("programResult.headerSection.graph.today")}
        </TagText>
        {weight !== undefined && (
          <Text
            color={CompassColor.white}
            fontFamily="Untitled Serif"
            fontSize="16px"
            fontWeight={500}
            lineHeight="24px"
          >
            {t("programResult.headerSection.graph.weight", { weight })}
          </Text>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        position="absolute"
        right="var(--spacing-l)"
        top="336px"
        textAlign="right"
      >
        <TagText color={CompassColor.white}>
          {t("programResult.headerSection.graph.goal")}
        </TagText>
        {idealWeight !== undefined && (
          <Text
            color={CompassColor.white}
            fontFamily="Untitled Serif"
            fontSize="16px"
            fontWeight={500}
            lineHeight="24px"
          >
            {t("programResult.headerSection.graph.weight", {
              weight: idealWeight,
            })}
          </Text>
        )}
      </Box>
    </>
  );
};

const GraphOverlayItemsWeight = ({
  weight,
  idealWeight,
}: {
  weight?: number;
  idealWeight?: number;
}) => (
  <>
    <GraphOverlayWeightValues weight={weight} idealWeight={idealWeight} />
    <ImprovedBodyCompositionTooltip
      left="50%"
      position="absolute"
      top="332px"
      transform="translateX(-50%)"
    />
  </>
);

const GraphOverlayItemsDiabetes = ({
  weight,
  idealWeight,
}: {
  weight?: number;
  idealWeight?: number;
}) => (
  <>
    <GraphOverlayWeightValues weight={weight} idealWeight={idealWeight} />
    <ImprovedBodyCompositionTooltip
      left="52px"
      position="absolute"
      top="316px"
    />
    <LowerA1cTooltip left="197px" position="absolute" top="370px" />
  </>
);

const GraphOverlayItemsMed = () => {
  const { t } = useTranslation();

  return (
    <>
      <Box
        position="absolute"
        right="var(--spacing-l)"
        textAlign="right"
        top="256px"
      >
        <Text
          color={CompassColor.white}
          fontFamily="Untitled Serif"
          fontSize="16px"
          lineHeight="24px"
        >
          {t("programResult.headerSection.graph.yourWeight")}
        </Text>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        position="absolute"
        right="var(--spacing-l)"
        textAlign="right"
        top="292px"
      >
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.lifestyle")}
        </CaptionText>
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.change")}
        </CaptionText>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        position="absolute"
        right="var(--spacing-l)"
        textAlign="right"
        top="392px"
      >
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.lifestyle")}
        </CaptionText>
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.change")}
        </CaptionText>
        <CaptionText color={CompassColor.white}>
          {t("programResult.headerSection.graph.medication")}
        </CaptionText>
      </Box>
      <Box position="absolute" left="var(--spacing-l)" top="500px">
        <Text
          color={CompassColor.white}
          fontFamily="Untitled Sans"
          fontSize="12px"
          fontWeight={700}
          lineHeight="15px"
        >
          {t("programResult.headerSection.graph.week1")}
        </Text>
      </Box>
      <Box position="absolute" right="var(--spacing-xl)" top="500px">
        <Text
          color={CompassColor.white}
          fontFamily="Untitled Sans"
          fontSize="12px"
          fontWeight={700}
          lineHeight="15px"
        >
          {t("programResult.headerSection.graph.week68")}
        </Text>
      </Box>
    </>
  );
};

const getGraphOverlayItems = (
  headerSectionType: HeaderSectionType,
  weight?: number,
  idealWeight?: number,
) => {
  const graphOverlayItemsMap = {
    [HeaderSectionType.WEIGHT]: (
      <GraphOverlayItemsWeight weight={weight} idealWeight={idealWeight} />
    ),
    [HeaderSectionType.DIABETES]: (
      <GraphOverlayItemsDiabetes weight={weight} idealWeight={idealWeight} />
    ),
    [HeaderSectionType.MED]: <GraphOverlayItemsMed />,
  };

  return graphOverlayItemsMap[headerSectionType];
};

export const ProgramResultHeaderSection = () => {
  const {
    graphImageUrl,
    headerSectionType,
    personalizedProgramTextKey,
    weightLossTooltipHeadingText,
    weightLossTooltipWeightText,
    firstName,
    weight,
    idealWeight,
  } = useProgramResult();
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        left="50%"
        position="absolute"
        top="var(--spacing-xxxl)"
        transform="translateX(-50%)"
        width="100%"
      >
        <Box
          backgroundColor={CompassColor.black}
          borderRadius="10px"
          lineHeight="0px"
          padding="var(--spacing-xs) var(--spacing-s)"
        >
          <TagText color={CompassColor.white}>
            {t("programResult.headerSection.pillText", { firstName })}
          </TagText>
        </Box>
        <Box display="flex" marginTop="var(--spacing-s)">
          <Text
            color={CompassColor.white}
            fontFamily="Untitled Serif"
            fontSize="28px"
            lineHeight="36px"
            textAlign="center"
          >
            <Trans
              i18nKey="programResult.headerSection.title"
              context={personalizedProgramTextKey}
            />
          </Text>
        </Box>
        <Tooltip
          backgroundColor={CompassColor.tarocco}
          marginTop="var(--spacing-l)"
          width="200px"
        >
          <CaptionText color={CompassColor.white}>
            {weightLossTooltipHeadingText}
          </CaptionText>
          <Text
            color={CompassColor.white}
            fontFamily="Untitled Sans"
            fontSize="18px"
            fontWeight={500}
            lineHeight="26px"
          >
            {weightLossTooltipWeightText}
          </Text>
        </Tooltip>
      </Box>
      {getGraphOverlayItems(headerSectionType, weight, idealWeight)}
      <Image src={graphImageUrl} />
    </Box>
  );
};
